import { A2A_TRANSFERS_REDUCER_KEY, A2A_TRANSFERS_VERSION_NUMBER } from 'app-components/flux/a2aTransfers';
import { ACCOUNTS_REDUCER_KEY, ACCOUNTS_VERSION_NUMBER } from 'app-components/flux/accounts';
import { ACCOUNTS_BALANCES_REDUCER_KEY } from 'app-components/flux/accounts-balances';
import { ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY } from 'app-components/flux/accounts-balances-history';
import { ACCOUNTS_VALUE_CHANGES_REDUCER_KEY } from 'app-components/flux/accounts-value-changes';
import { ACHIEVEMENTS_REDUCER_KEY } from 'app-components/flux/achievements';
import { REDUCER_KEY as ALERT_RULES_REDUCER_KEY, ALERT_RULES_VERSION_NUMBER } from 'app-components/flux/alert-rules';
import { ALERTS_REDUCER_KEY } from 'app-components/flux/alerts';
import { AUTH_REDUCER_KEY } from 'app-components/flux/auth';
import { BILL_PRESENTMENT_REDUCER_KEY, BILL_PRESENTMENT_VERSION } from 'app-components/flux/bill-presentment';
import { CATEGORIES_REDUCER_KEY, CATEGORIES_VERSION_NUMBER } from 'app-components/flux/categories';
import { CUSTOMERS_VERSION_NUMBER } from 'app-components/flux/customers';
import { CUSTOMERS_REDUCER_KEY } from 'app-components/flux/customers/customersTypes';
import { DATASETS_REDUCER_KEY } from 'app-components/flux/datasets';
import { DOCUMENTS_REDUCER_KEY } from 'app-components/flux/documents';
import {
  DOMAIN_LOGOS_REDUCER_KEY,
  DOMAIN_LOGOS_VERSION_NUMBER,
  stateToCacheDomainLogos,
} from 'app-components/flux/domain-logos/domainLogosTypes';
import { ENTITLEMENTS_REDUCER_KEY } from 'app-components/flux/entitlements';
import { FEATURE_FLAGS_REDUCER_KEY } from 'app-components/flux/feature-flags';
import { FILTERS_REDUCER_KEY } from 'app-components/flux/filters';
import { FREE_TO_SPEND_REDUCER_KEY } from 'app-components/flux/free-to-spend';
import {
  GENERATED_REPORTS_REDUCER_KEY,
  GENERATED_REPORTS_VERSION_NUMBER,
  generatedReportsPersistence,
} from 'app-components/flux/generated-reports';
import { GOALS_REDUCER_KEY } from 'app-components/flux/goals';
import {
  INSTITUTION_LOGINS_REDUCER_KEY,
  INSTITUTION_LOGINS_VERSION_NUMBER,
  institutionLoginsTransformers,
} from 'app-components/flux/institution-logins';
import { INSTITUTIONS_REDUCER_KEY } from 'app-components/flux/institutions';
import {
  INVESTMENT_HOLDINGS_V2_REDUCER_KEY,
  INVESTMENT_HOLDINGS_V2_VERSION_NUMBER,
} from 'app-components/flux/investment-holdings-v2';
import { INVESTMENT_IRR_REDUCER_KEY } from 'app-components/flux/investment-irr';
import { INVESTMENT_QUOTES_V2_REDUCER_KEY } from 'app-components/flux/investment-quotes-v2';
import { INVESTMENT_SYMBOLS_KEY } from 'app-components/flux/investment-symbols';
import { INVESTMENT_TWRR_REDUCER_KEY } from 'app-components/flux/investment-twrr';
import { INVOICE_ITEMS_REDUCER_KEY } from 'app-components/flux/invoice-items/invoiceItemTypes';
import {
  cacheToStateInvoiceItems,
  shouldPersistInvoiceItems,
  stateToCacheInvoiceItems,
} from 'app-components/flux/invoice-items/invoiceItemsSlice';
import { MILEAGE_TRACKER_REDUCER_KEY } from 'app-components/flux/mileage-tracker/mileageTrackerTypes';
import {
  cacheToStateMileageTracker,
  shouldPersistMileageTracker,
  stateToCacheMileageTracker,
} from 'app-components/flux/mileage-tracker/mileageTrackerSlice';
import { INVESTMENTS_NEWSFEED_REDUCER_KEY } from 'app-components/flux/investments-newsfeed';
import { MILESTONE_REDUCER_KEY } from 'app-components/flux/milestones';
import { POSTPONED_ACTIONS_REDUCER_KEY } from 'app-components/flux/postponed-actions';
import { PREFERENCES_V2_REDUCER_KEY } from 'app-components/flux/preferences-v2';
import { PROFILE_REDUCER_KEY, PROFILE_VERSION_NUMBER } from 'app-components/flux/profile';
import { PROJECTS_REDUCER_KEY } from 'app-components/flux/projects/projectsTypes';
import {
  SCHEDULED_TRANSACTIONS_REDUCER_KEY,
  SCHEDULED_TRANSACTIONS_VERSION_NUMBER,
} from 'app-components/flux/scheduled-transactions';
import { SECURITIES_REDUCER_KEY } from 'app-components/flux/securities';
import { SPENDING_WATCH_LIST_REDUCER_KEY } from 'app-components/flux/spending-watchlist';
import { SUBSCRIPTIONS_REDUCER_KEY } from 'app-components/flux/subscriptions';
import { TAGS_REDUCER_KEY } from 'app-components/flux/tags';
import { TAXES_REDUCER_KEY, TAXES_VERSION } from 'app-components/flux/taxes/taxesTypes';
import {
  TICKER_LOGOS_REDUCER_KEY,
  TICKER_LOGOS_VERSION_NUMBER,
} from 'app-components/flux/ticker-logos/tickerLogosTypes';
import { TRANSACTIONS_REDUCER_KEY, TRANSACTIONS_VERSION_NUMBER } from 'app-components/flux/transactions';
import {
  cacheToStateTransactions,
  stateToCacheTransactions,
} from 'app-components/flux/transactions/transactionsTransformers';
import {
  INVESTMENTS_QUOTES_DETAILED_REDUCER_KEY,
  INVESTMENTS_QUOTES_DETAILED_VERSION_NUMBER,
} from 'app-components/flux/investments-quotes-detailed/investmentsQuotesDetailedTypes';

import type { PersistableStateConfig } from 'app-components/utils/persist';
import { initializeConfig } from 'app-components/utils/persist';

import { APP_REDUCER_KEY } from 'data/app/reducer';
import { FUNDING_ACCOUNTS_REDUCER_KEY } from 'data/fundingAccounts/reducer';
import { REDUCER_KEY as PREFERENCES_REDUCER_KEY } from 'data/preferences/reducer';
import { TRANSFER_BALANCE_REDUCER_KEY } from 'data/transferBalance/transferBalanceReducer';
import { TRANSFER_HISTORY_REDUCER_KEY } from 'data/transferHistory/transferHistoryReducer';

import localForage from 'localforage';
import {
  cacheToStateCreditScore,
  creditScoreCacheSlice,
  stateToCacheCreditScore,
} from 'data/creditScoreCache/creditScoreCacheSlice';

const cacheKeyPrefix = (userId: string, datasetId: string) => `${userId}:${datasetId}:`;

/** Whether this browser stores a cache for the user's dataset, for example from a tab closed without signing out. */
export async function hasCachedState(userId: string, datasetId: string): Promise<boolean> {
  const prefix = cacheKeyPrefix(userId, datasetId);
  try {
    const keys = await localForage.keys();
    return keys.some((key) => key.startsWith(prefix));
  } catch {
    // Unreadable storage has nothing a page load could restore either.
    return false;
  }
}

export function createPersistConfig(userId: string, datasetId: string) {
  const persistConfig = {
    cacheMetadataKey: 'simplifiCacheMeta',
    encryptionKey: `${userId}:${datasetId}`,
    getCacheKeyForStateKey: (stateKey: string) => `${cacheKeyPrefix(userId, datasetId)}${stateKey}`,
    // Keep these sorted by key name. Lint can't check computed keys.
    persistableStateConfig: {
      [A2A_TRANSFERS_REDUCER_KEY]: { version: A2A_TRANSFERS_VERSION_NUMBER },
      [ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY]: { version: 1 },
      [ACCOUNTS_BALANCES_REDUCER_KEY]: {
        version: 1,
      },
      [ACCOUNTS_REDUCER_KEY]: { version: ACCOUNTS_VERSION_NUMBER },
      [ACCOUNTS_VALUE_CHANGES_REDUCER_KEY]: { version: 1 },
      [ACHIEVEMENTS_REDUCER_KEY]: { version: 1 },
      [ALERTS_REDUCER_KEY]: { version: 2 },
      [ALERT_RULES_REDUCER_KEY]: { version: ALERT_RULES_VERSION_NUMBER },
      [APP_REDUCER_KEY]: { version: 1 },
      [AUTH_REDUCER_KEY]: {
        encrypt: true,
        version: 1,
      },
      [BILL_PRESENTMENT_REDUCER_KEY]: { version: BILL_PRESENTMENT_VERSION },
      [CATEGORIES_REDUCER_KEY]: { version: CATEGORIES_VERSION_NUMBER },
      [creditScoreCacheSlice.reducerPath]: {
        cacheToState: cacheToStateCreditScore,
        encrypt: true,
        stateToCache: stateToCacheCreditScore,
        version: 1,
      },
      [CUSTOMERS_REDUCER_KEY]: { version: CUSTOMERS_VERSION_NUMBER },
      [DATASETS_REDUCER_KEY]: { version: 1 },
      [DOCUMENTS_REDUCER_KEY]: { version: 1 },
      [DOMAIN_LOGOS_REDUCER_KEY]: {
        stateToCache: stateToCacheDomainLogos,
        version: DOMAIN_LOGOS_VERSION_NUMBER,
      },
      [ENTITLEMENTS_REDUCER_KEY]: {
        encrypt: true,
        version: 1,
      },
      [FEATURE_FLAGS_REDUCER_KEY]: { version: 1 },
      [FILTERS_REDUCER_KEY]: { version: 1 },
      [FREE_TO_SPEND_REDUCER_KEY]: { version: 1 },
      [FUNDING_ACCOUNTS_REDUCER_KEY]: { version: 2 },
      [GENERATED_REPORTS_REDUCER_KEY]: {
        shouldPersist: generatedReportsPersistence.shouldPersistGeneratedReports,
        stateToCache: generatedReportsPersistence.stateToCacheGeneratedReports,
        version: GENERATED_REPORTS_VERSION_NUMBER,
      },
      [GOALS_REDUCER_KEY]: { version: 2 },
      [INSTITUTIONS_REDUCER_KEY]: { version: 1 },
      [INSTITUTION_LOGINS_REDUCER_KEY]: {
        cacheToState: institutionLoginsTransformers.cacheToStateInstitutionLogins,
        encrypt: true,
        stateToCache: institutionLoginsTransformers.stateToCacheInstitutionLogins,
        version: INSTITUTION_LOGINS_VERSION_NUMBER,
      },
      [INVESTMENTS_NEWSFEED_REDUCER_KEY]: { version: 1 },
      [INVESTMENTS_QUOTES_DETAILED_REDUCER_KEY]: { version: INVESTMENTS_QUOTES_DETAILED_VERSION_NUMBER },
      [INVESTMENT_HOLDINGS_V2_REDUCER_KEY]: { version: INVESTMENT_HOLDINGS_V2_VERSION_NUMBER },
      [INVESTMENT_IRR_REDUCER_KEY]: { version: 1 },
      [INVESTMENT_QUOTES_V2_REDUCER_KEY]: { version: 1 },
      [INVESTMENT_SYMBOLS_KEY]: { version: 1 },
      [INVESTMENT_TWRR_REDUCER_KEY]: { version: 1 },
      [INVOICE_ITEMS_REDUCER_KEY]: {
        cacheToState: cacheToStateInvoiceItems,
        encrypt: true,
        shouldPersist: shouldPersistInvoiceItems,
        stateToCache: stateToCacheInvoiceItems,
        version: 1,
      },
      // Trips include addresses, so they are encrypted like other personal data.
      [MILEAGE_TRACKER_REDUCER_KEY]: {
        cacheToState: cacheToStateMileageTracker,
        encrypt: true,
        shouldPersist: shouldPersistMileageTracker,
        stateToCache: stateToCacheMileageTracker,
        version: 1,
      },
      [MILESTONE_REDUCER_KEY]: { version: 1 },
      [POSTPONED_ACTIONS_REDUCER_KEY]: { version: 1 },
      [PREFERENCES_REDUCER_KEY]: { version: 1 },
      [PREFERENCES_V2_REDUCER_KEY]: { version: 1 },
      [PROFILE_REDUCER_KEY]: {
        encrypt: true,
        version: PROFILE_VERSION_NUMBER,
      },
      [PROJECTS_REDUCER_KEY]: { version: 1 },
      [SCHEDULED_TRANSACTIONS_REDUCER_KEY]: { version: SCHEDULED_TRANSACTIONS_VERSION_NUMBER },
      [SECURITIES_REDUCER_KEY]: { version: 1 },
      [SPENDING_WATCH_LIST_REDUCER_KEY]: { version: 1 },
      [SUBSCRIPTIONS_REDUCER_KEY]: { version: 1 },
      [TAGS_REDUCER_KEY]: { version: 1 },
      [TAXES_REDUCER_KEY]: { version: TAXES_VERSION },
      [TICKER_LOGOS_REDUCER_KEY]: { version: TICKER_LOGOS_VERSION_NUMBER },
      [TRANSACTIONS_REDUCER_KEY]: {
        // The persist layer hands back the parsed stateToCache output, typed as unknown.
        cacheToState: (cache) => cacheToStateTransactions(cache as Parameters<typeof cacheToStateTransactions>[0]),
        stateToCache: stateToCacheTransactions,
        version: TRANSACTIONS_VERSION_NUMBER,
      },
      [TRANSFER_BALANCE_REDUCER_KEY]: { version: 1 },
      [TRANSFER_HISTORY_REDUCER_KEY]: { version: 1 },
    } satisfies PersistableStateConfig,
    storageInterface: localForage,
    version: 2,
  };

  return initializeConfig(persistConfig);
}
